/* eslint-disable max-len */
import {
  INTROSPECTION_QUERY,
  GET_SEASONS_TEAMS_GAMES_PLAYERS,
  GET_SEASONS_TEAMS_PLAYERS,
  GET_SEASONS_TEAMS_GAMES,
  GET_SEASON_GAMES_PLAYERS,
  GET_SEASON_GAMES,
  GET_SEASONS_TEAMS,
  GET_TEAM_SEASON_WITH_GAMES_PLAYERS,
  GET_TEAM_SEASON_WITH_GAMES,
  GET_SEASON_PLAYERS,
  GET_TEAM_SEASON_WITH_PLAYERS,
  GET_PLAYER_GAME_STATS,
  GET_PLAYER_SEASON_STATS,
  GET_TEAM_GAME_STATS,
  GET_TEAM_SEASON_STATS,
} from '@tagboard/tgb-gql-helpers';

import { apolloClient } from './apollo-client';

const SPORTRADAR_BASE = process.env.SPORTRADAR_API;

const getHeaders = (ownerId) => {
  const headers = {
    'tgb-owner-id': ownerId,
  };

  const displayToken = window.$vm?.$route?.query?.dt;
  if (displayToken) {
    headers['tgb-display-token'] = displayToken;
  }

  return headers;
};

export default {
  async getSchema() {
    try {
      const { data, error } = await apolloClient.query({
        query: INTROSPECTION_QUERY,
      });

      if (error) {
        throw new Error(error);
      }
      return data;
    } catch (error) {
      console.error('Error fetching sportradar schema:', error);
      return {};
    }
  },
  async getStatSelectionData({
    leagueName,
    seasonId,
    teamId,
    ownerId,
    statPeriod,
    statType,
  }) {
    const isPlayerGameStats = statType === 'player' && statPeriod === 'game';
    const isPlayerSeasonStats = statType === 'player' && statPeriod === 'season';
    const isTeamGameStats = statType === 'team' && statPeriod === 'game';

    let query;
    if (isPlayerGameStats) {
      query = GET_SEASONS_TEAMS_GAMES_PLAYERS;
    } else if (isTeamGameStats) {
      query = GET_SEASONS_TEAMS_GAMES;
    } else if (isPlayerSeasonStats) {
      query = GET_SEASONS_TEAMS_PLAYERS;
    } else {
      // if controls are not provided, we will default to get all the things since this is new
      query = GET_SEASONS_TEAMS_GAMES_PLAYERS;
    }

    const { data } = await apolloClient.query({
      query,
      variables: { leagueName, seasonId, teamId },
      context: {
        headers: getHeaders(ownerId),
      },
    });

    return data.getGraphicsConnectModal;
  },
  async statSelectorTeamChange({
    leagueName, ownerId, seasonId, teamId, statPeriod, statType,
  }) {
    const isPlayerGameStats = statType === 'player' && statPeriod === 'game';
    const isPlayerSeasonStats = statType === 'player' && statPeriod === 'season';
    const isTeamGameStats = statType === 'team' && statPeriod === 'game';

    let query;
    if (isPlayerGameStats) {
      query = GET_TEAM_SEASON_WITH_GAMES_PLAYERS;
    } else if (isTeamGameStats) {
      query = GET_TEAM_SEASON_WITH_GAMES;
    } else if (isPlayerSeasonStats) {
      query = GET_TEAM_SEASON_WITH_PLAYERS;
    } else {
      query = GET_SEASONS_TEAMS;
    }

    const { data } = await apolloClient.query({
      query,
      variables: { leagueName, seasonId, teamId },
      context: {
        headers: getHeaders(ownerId),
      },
    });
    return data.getGraphicsConnectModal;
  },
  async statSelectorSeasonChange({
    leagueName, ownerId, seasonId, teamId, statPeriod, statType,
  }) {
    const isPlayerGameStats = statType === 'player' && statPeriod === 'game';
    const isPlayerSeasonStats = statType === 'player' && statPeriod === 'season';
    const isTeamGameStats = statType === 'team' && statPeriod === 'game';
    const isTeamSeasonStats = statType === 'team' && statPeriod === 'season';

    let query;

    if (isPlayerGameStats) {
      query = GET_SEASON_GAMES_PLAYERS;
    } else if (isTeamGameStats) {
      query = GET_SEASON_GAMES;
    } else if (isPlayerSeasonStats) {
      query = GET_SEASON_PLAYERS;
    } else if (isTeamSeasonStats) {
      query = GET_SEASONS_TEAMS;
    }

    const { data } = await apolloClient.query({
      query,
      variables: { leagueName, seasonId, teamId },
      context: {
        headers: getHeaders(ownerId),
      },
    });
    return data.getGraphicsConnectModal;
  },
  // TODO: will be replaced with apollo client calls
  // currently only used for images
  async getSportradarData(opts = {}) {
    const {
      leagueName,
      route,
      query,
    } = opts;

    const queryString = Object.keys(query).map((k) => `${k}=${query[k]}`).join('&');

    const response = await fetch(`${SPORTRADAR_BASE}/${leagueName}/${route}?${queryString}`, {
      mode: 'cors',
      credentials: 'include',
    });

    if (response?.ok) {
      return response.json();
    }

    throw new Error(`Error in sportradar response ${response.status} ${response.statusText}`);
  },

  async getSportradarGraphQlData(opts = {}) {
    const {
      ownerId,
      seasonId,
      teamId,
      playerId,
      gameId,
      leagueName,
      statType,
      statPeriod,
    } = opts;

    const isPlayerGameStats = statType === 'player' && statPeriod === 'game';
    const isPlayerSeasonStats = statType === 'player' && statPeriod === 'season';
    const isTeamGameStats = statType === 'team' && statPeriod === 'game';
    const isTeamSeasonStats = statType === 'team' && statPeriod === 'season';

    let query;
    let variables;
    let resolverName;

    if (isPlayerGameStats) {
      query = GET_PLAYER_GAME_STATS;
      resolverName = 'getPlayerGameStats';
      variables = { playerId, gameId, leagueName };
    } else if (isPlayerSeasonStats) {
      query = GET_PLAYER_SEASON_STATS;
      resolverName = 'getPlayerSeasonStats';
      variables = {
        playerId, teamId, seasonId, leagueName,
      };
    } else if (isTeamGameStats) {
      query = GET_TEAM_GAME_STATS;
      resolverName = 'getTeamGameStats';
      variables = { teamId, gameId, leagueName };
    } else if (isTeamSeasonStats) {
      query = GET_TEAM_SEASON_STATS;
      resolverName = 'getTeamSeasonStats';
      variables = { teamId, seasonId, leagueName };
    } else {
      query = GET_SEASONS_TEAMS;
      variables = { seasonId, leagueName };
    }

    try {
      const { data, error } = await apolloClient.query({
        query,
        variables,
        context: {
          headers: getHeaders(ownerId),
        },
      });

      if (error) {
        throw new Error(error);
      }

      return data[resolverName];
    } catch (error) {
      console.error('Error fetching sportradar graphql data:', error);
      return {};
    }
  },
};
